import { useEffect, useState } from "react";

import type {
  EchoflicksSessionJourney,
  EchoflicksSessionMedia,
  EchoflicksVideoSession,
  I2VPromptAudit,
} from "../types";
import type {
  SceneI2VAudit,
  SceneI2VProviderAttempt,
  SceneI2VReferenceFrame,
  SceneI2VReferenceFrames,
} from "./scriptStory";
import type { MediaUrlSource } from "./useMediaUrl";
import { useMediaUrl } from "./useMediaUrl";

// ---------------------------------------------------------------------------
// Effective I2V prompt: the real prompt that was sent to Kling / Veo.
//
// It lives in the durable `echo-state.v1.json` manifest (per scene under
// `scenes[].i2v`) at the deterministic key
// `video-generation/{user_id}/echoes/{workload_id}/echo-state.v1.json`, and is
// also published per attempt as a `scene.i2v.prompt_resolved` journey node.
//
// `useEchoStateManifest` resolves the manifest URL (3 ways — see
// `resolveManifestSource`), fetches it with the same signed-URL / CORS path the
// script envelope uses (`useMediaUrl`), and returns a
// `Map<sceneId, SceneI2VAudit>`. `deriveJourneyI2V` builds the same map from the
// already-fetched journey nodes as a fallback, and `mergeSceneI2V` joins them
// (manifest wins, journey fills the gaps).
// ---------------------------------------------------------------------------

export const ECHO_STATE_MANIFEST_KIND = "echo_state_manifest";
const MANIFEST_FILENAME = "echo-state.v1.json";

export type ManifestStatus =
  | "idle"
  | "loading"
  | "loaded"
  | "error"
  | "unavailable";

export type EchoStateManifestState = {
  status: ManifestStatus;
  byScene: Map<string | number, SceneI2VAudit>;
  /**
   * The raw parsed manifest body, retained for consumers that read fields
   * beyond I2V (the Director View needs `beat_sync`, `workload_id`,
   * `realized_scene_timeline`, per-scene `framing_plan`). Null until loaded.
   */
  body: unknown;
  message?: string;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function str(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value : null;
}

function bool(value: unknown): boolean | undefined {
  return typeof value === "boolean" ? value : undefined;
}

function num(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function stringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter((item): item is string => typeof item === "string")
    .map((item) => item.trim())
    .filter(Boolean);
}

function mapProviderHistory(
  value: unknown,
): SceneI2VProviderAttempt["providerHistory"] {
  if (!Array.isArray(value)) return [];
  return value.filter(isRecord).map((item) => ({
    concreteProvider: str(item.concrete_provider_id),
    outcome: str(item.outcome),
    errorCode: str(item.error_code),
    retryable: bool(item.retryable),
    taskId: str(item.task_id),
  }));
}

export function mapI2VAttempt(value: unknown): SceneI2VProviderAttempt | null {
  if (!isRecord(value)) return null;
  const errorMessage = str(value.provider_error_message);
  const errorCode = str(value.provider_error_code);
  const classified = classifyLegacyI2VFailure(errorCode, errorMessage);
  return {
    attempt: num(value.attempt),
    provider: str(value.provider),
    concreteProvider: str(value.concrete_provider),
    model: str(value.model),
    modelVersion: str(value.model_version),
    mode: str(value.mode),
    lifecycleStatus: str(value.lifecycle_status),
    qcDisposition: str(value.qc_disposition),
    taskId: str(value.task_id),
    finalS3Key: str(value.final_s3_key),
    errorCode,
    errorMessage,
    errorCategory:
      str(value.provider_error_category) ?? classified?.errorCategory ?? null,
    errorOrigin:
      str(value.provider_error_origin) ?? classified?.errorOrigin ?? null,
    displayMessage:
      str(value.provider_error_display_message) ??
      classified?.displayMessage ??
      null,
    supportCodes: [
      ...new Set([
        ...stringArray(value.provider_error_support_codes),
        ...(classified?.supportCodes ?? []),
      ]),
    ],
    providerHistory: mapProviderHistory(value.provider_attempt_history),
  };
}

function mapI2VAttempts(value: unknown): SceneI2VProviderAttempt[] {
  if (!Array.isArray(value)) return [];
  return value
    .map(mapI2VAttempt)
    .filter((item): item is SceneI2VProviderAttempt => item !== null);
}

function classifyLegacyI2VFailure(
  errorCode: string | null,
  errorMessage: string | null,
) {
  const message = errorMessage ?? "";
  const supportCodes = [...new Set(message.match(/\b\d{8}\b/g) ?? [])];
  if (supportCodes.some((code) => ["17301594", "58061214"].includes(code))) {
    return {
      errorCategory: "minor",
      errorOrigin: "input_media",
      displayMessage: "Veo rejected the input because it may depict a minor.",
      supportCodes,
    };
  }
  if (supportCodes.some((code) => ["29310472", "15236754"].includes(code))) {
    return {
      errorCategory: "celebrity",
      errorOrigin: "input_media",
      displayMessage:
        "Veo rejected the input because it may depict a celebrity or prominent person.",
      supportCodes,
    };
  }
  if (
    message.toLowerCase().includes("aspect ratio") ||
    errorCode?.toLowerCase().includes("aspect_mismatch")
  ) {
    return {
      errorCategory: "aspect_mismatch",
      errorOrigin: "input_media",
      displayMessage:
        "Kling rejected the prepared source because its aspect ratio did not match the request.",
      supportCodes,
    };
  }
  return null;
}

/**
 * Build the conventional manifest key for an echo. The composer always writes the
 * manifest at the deterministic `video-generation/{user_id}/echoes/{workload_id}/
 * echo-state.v1.json`, so when `user_id` + `workload_id` are known that canonical
 * key is preferred. Sibling derivation is a *narrow* last resort: it is only safe
 * when the script itself lives in the `…/echoes/{workload_id}/` folder (refined
 * echoes write `orch-script.v1.json` there) — a normal echo's script sits one level
 * up at `video-generation/{user_id}/<file>.json`, so deriving a sibling there would
 * drop the `echoes/{workload_id}/` segment and point at the wrong key.
 */
export function buildManifestKey(args: {
  siblingKey?: string | null; // e.g. the script_storage_path
  userId?: string | null;
  workloadId?: string | null;
}): string | null {
  const userId = str(args.userId ?? null);
  const workloadId = str(args.workloadId ?? null);
  if (userId && workloadId) {
    return `video-generation/${userId}/echoes/${workloadId}/${MANIFEST_FILENAME}`;
  }
  // Last resort: only when the sibling is itself inside an `…/echoes/…/` folder.
  const sibling = str(args.siblingKey ?? null);
  if (sibling && sibling.includes("/echoes/")) {
    const dir = sibling.slice(0, sibling.lastIndexOf("/"));
    if (dir) return `${dir}/${MANIFEST_FILENAME}`;
  }
  return null;
}

/**
 * Resolve where the echo-state manifest lives, in precedence order:
 *   1. a media row with `kind === "echo_state_manifest"` (added by infra);
 *   2. an explicit session field (`manifest_storage_path` / `manifest_s3_key`);
 *   3. the conventional key derived from the script's sibling folder, or from
 *      `{user_id}/{workload_id}` (the deterministic last resort).
 * Returns a `MediaUrlSource` (the same shape `useMediaUrl` signs), or null when
 * nothing is resolvable (old / manifest-off echoes).
 */
export function resolveManifestSource(
  session: EchoflicksVideoSession | null | undefined,
  media: EchoflicksSessionMedia[],
): MediaUrlSource | null {
  if (!session) return null;

  // 1. explicit media row
  const manifestMedia = media.find(
    (item) => item.kind === ECHO_STATE_MANIFEST_KIND,
  );
  if (manifestMedia?.storage_path) {
    return {
      url: manifestMedia.url ?? null,
      storage_bucket: manifestMedia.storage_bucket ?? manifestMedia.bucket,
      storage_path: manifestMedia.storage_path,
      storage_is_public: manifestMedia.storage_is_public ?? false,
    };
  }

  // 2. explicit session field
  const sessionKey =
    session.manifest_storage_path ?? session.manifest_s3_key ?? null;
  const bucket =
    session.manifest_storage_bucket ?? session.script_storage_bucket ?? null;
  if (sessionKey) {
    return {
      url: null,
      storage_bucket: bucket,
      storage_path: sessionKey,
      storage_is_public: false,
    };
  }

  // 3. conventional key (sibling to the script, else {user_id}/{workload_id})
  const conventionKey = buildManifestKey({
    siblingKey: session.script_storage_path,
    userId: session.whatsapp_user_id,
    workloadId: session.workload_id,
  });
  if (conventionKey && bucket) {
    return {
      url: null,
      storage_bucket: bucket,
      storage_path: conventionKey,
      storage_is_public: false,
    };
  }

  return null;
}

/** Map one manifest `scenes[].i2v.reference_frames.<frame>` dict to a reference frame. */
function mapReferenceFrame(value: unknown): SceneI2VReferenceFrame | null {
  if (!isRecord(value)) return null;
  return {
    fileName: str(value.file_name),
    storageKey: str(value.storage_key),
    role: str(value.role),
  };
}

/** Map the manifest `scenes[].i2v.reference_frames` dict to `SceneI2VReferenceFrames`. */
export function mapReferenceFrames(
  value: unknown,
): SceneI2VReferenceFrames | null {
  if (!isRecord(value)) return null;
  const referenceFiles = Array.isArray(value.reference_files)
    ? value.reference_files.map(String)
    : null;
  return {
    firstFrame: mapReferenceFrame(value.first_frame),
    endFrame: mapReferenceFrame(value.end_frame),
    hasEndFrame: bool(value.has_end_frame) ?? isRecord(value.end_frame),
    referenceFiles:
      referenceFiles && referenceFiles.length ? referenceFiles : null,
  };
}

/** Map one manifest `scenes[].i2v` dict (snake_case) to a `SceneI2VAudit`. */
export function mapManifestI2V(i2v: Record<string, unknown>): SceneI2VAudit {
  return {
    finalPositivePrompt: str(i2v.final_positive_prompt),
    negativePrompt: str(i2v.negative_prompt),
    scenePrompt: str(i2v.scene_prompt),
    sourcePreservationPrompt: str(i2v.source_preservation_prompt),
    provider: str(i2v.provider),
    concreteProvider: str(i2v.concrete_provider),
    model: str(i2v.model),
    attemptCount: num(i2v.attempt_count),
    fallbackApplied: bool(i2v.fallback_applied) ?? false,
    fallbackFrom: str(i2v.fallback_from),
    fallbackTo: str(i2v.fallback_to),
    fallbackReason: str(i2v.fallback_reason),
    raiFiltered: bool(i2v.rai_filtered) ?? false,
    raiReason: str(i2v.rai_reason),
    animated: bool(i2v.animated),
    finalSceneType: str(i2v.final_scene_type),
    finalOutputKind: str(i2v.final_output_kind),
    staticFallbackApplied: bool(i2v.static_fallback_applied),
    taskId: str(i2v.task_id),
    attempts: mapI2VAttempts(i2v.attempts),
    referenceFrames: mapReferenceFrames(i2v.reference_frames),
    source: "manifest",
  };
}

/**
 * Resolve one scene's audit from a merged by-scene map: scene id (raw, then
 * string form), then the positional `index:{n}` fallback — the single key
 * ladder every consumer of these maps must use, since `parseManifestI2V` keys
 * scenes both ways depending on whether the manifest carried a scene id.
 */
export function lookupSceneAudit(
  byScene: Map<string | number, SceneI2VAudit> | null | undefined,
  scene: { index: number; sceneId?: number | string | null },
): SceneI2VAudit | null {
  if (!byScene) return null;
  const { sceneId, index } = scene;
  const byId =
    sceneId != null
      ? (byScene.get(sceneId) ?? byScene.get(String(sceneId)))
      : undefined;
  return byId ?? byScene.get(`index:${index}`) ?? null;
}

/**
 * Parse a parsed `echo-state.v1.json` body into a `Map<sceneId, SceneI2VAudit>`.
 * Scenes are keyed by `scene_id` (raw + string form); scenes without an id are
 * keyed positionally as `index:{n}` to mirror `buildStoryboard`'s fallback.
 */
export function parseManifestI2V(
  manifest: unknown,
): Map<string | number, SceneI2VAudit> {
  const byScene = new Map<string | number, SceneI2VAudit>();
  if (!isRecord(manifest)) return byScene;
  const scenes = manifest.scenes;
  if (!Array.isArray(scenes)) return byScene;

  scenes.forEach((scene, index) => {
    if (!isRecord(scene)) return;
    const i2v = scene.i2v;
    if (!isRecord(i2v)) return;
    const audit = mapManifestI2V(i2v);
    const sceneId = scene.scene_id ?? scene.id ?? null;
    if (typeof sceneId === "number" || typeof sceneId === "string") {
      byScene.set(sceneId, audit);
      byScene.set(String(sceneId), audit);
    } else {
      byScene.set(`index:${index}`, audit);
    }
  });

  return byScene;
}

// ---------------------------------------------------------------------------
// Journey fallback (source B): derive the effective prompt from the
// `scene.i2v.prompt_resolved` journey nodes the page already fetched.
// ---------------------------------------------------------------------------

const PROMPT_RESOLVED_TYPES = new Set([
  "scene.i2v.prompt_resolved",
  "i2v_prompt",
]);

type JourneyAuditCandidate = {
  sceneId: string | number | null;
  attempt: number;
  disposition: string | null;
  audit: SceneI2VAudit;
};

function auditFromPromptResolved(
  payload: Record<string, unknown>,
): SceneI2VAudit | null {
  const auditObj = isRecord(payload.i2v_prompt_audit)
    ? (payload.i2v_prompt_audit as I2VPromptAudit & Record<string, unknown>)
    : null;
  const summary = isRecord(payload.i2v_prompt_audit_summary)
    ? (payload.i2v_prompt_audit_summary as Record<string, unknown>)
    : null;
  if (!auditObj && !summary) return null;

  const prompts = isRecord(auditObj?.prompts) ? auditObj!.prompts : null;
  const auditProvider = isRecord(auditObj?.provider) ? auditObj.provider : null;
  const provider =
    str(summary?.provider) ??
    str(summary?.requested_provider) ??
    str(auditProvider?.requested_provider) ??
    str(auditProvider?.name);

  return {
    finalPositivePrompt: str(prompts?.final_positive_prompt),
    negativePrompt: str(prompts?.negative_prompt),
    scenePrompt: str(prompts?.scene_prompt),
    sourcePreservationPrompt: str(prompts?.source_preservation_prompt),
    provider,
    concreteProvider: str(summary?.concrete_provider),
    model: str(summary?.model),
    attemptCount: num(summary?.attempt ?? auditObj?.attempt),
    fallbackApplied: false,
    raiFiltered: false,
    finalSceneType: str(
      summary?.source_scene_type ?? auditObj?.source_scene_type,
    ),
    taskId: str(summary?.task_id),
    attempts: [],
    referenceFrames: mapReferenceFrames(
      auditObj?.reference_frames ?? summary?.reference_frames,
    ),
    source: "journey",
  };
}

/**
 * Build the per-scene I2V map from journey nodes. When several attempts exist
 * for a scene, keep the accepted one (`qc_disposition === "accepted"`), else the
 * highest attempt seen.
 */
export function deriveJourneyI2V(
  journey: EchoflicksSessionJourney | undefined | null,
): Map<string | number, SceneI2VAudit> {
  const best = new Map<string, JourneyAuditCandidate>();

  for (const node of journey?.nodes ?? []) {
    if (!PROMPT_RESOLVED_TYPES.has(node.type)) continue;
    const payload = isRecord(node.raw_payload) ? node.raw_payload : {};
    // The mapped node also lifts these to the top level.
    const merged: Record<string, unknown> = {
      i2v_prompt_audit: node.i2v_prompt_audit ?? payload.i2v_prompt_audit,
      i2v_prompt_audit_summary:
        node.i2v_prompt_audit_summary ?? payload.i2v_prompt_audit_summary,
    };
    const audit = auditFromPromptResolved(merged);
    if (!audit) continue;

    const summary = isRecord(merged.i2v_prompt_audit_summary)
      ? (merged.i2v_prompt_audit_summary as Record<string, unknown>)
      : null;
    const sceneId =
      (typeof summary?.scene_id === "number" ||
      typeof summary?.scene_id === "string"
        ? summary.scene_id
        : null) ?? null;
    if (sceneId == null) continue;

    const attempt = num(summary?.attempt) ?? 0;
    const disposition =
      str(summary?.asset_disposition) ?? str(payload.qc_disposition);
    const key = String(sceneId);
    const candidate: JourneyAuditCandidate = {
      sceneId,
      attempt,
      disposition,
      audit: { ...audit, attemptCount: audit.attemptCount ?? attempt },
    };

    const prev = best.get(key);
    if (!prev || preferCandidate(candidate, prev)) {
      best.set(key, candidate);
    }
  }

  const byScene = new Map<string | number, SceneI2VAudit>();
  for (const candidate of best.values()) {
    if (candidate.sceneId == null) continue;
    byScene.set(candidate.sceneId, candidate.audit);
    byScene.set(String(candidate.sceneId), candidate.audit);
  }
  for (const node of journey?.nodes ?? []) {
    if (node.type !== "scene.i2v.attempt_finished") continue;
    const payload = isRecord(node.raw_payload) ? node.raw_payload : {};
    const summary = isRecord(payload.i2v_prompt_audit_summary)
      ? payload.i2v_prompt_audit_summary
      : isRecord(node.i2v_prompt_audit_summary)
        ? node.i2v_prompt_audit_summary
        : null;
    const sceneId =
      typeof summary?.scene_id === "number" ||
      typeof summary?.scene_id === "string"
        ? summary.scene_id
        : null;
    if (sceneId == null) continue;
    const existing = byScene.get(sceneId) ?? byScene.get(String(sceneId));
    if (!existing) continue;
    const attempt = mapI2VAttempt({
      attempt: summary?.attempt,
      provider: summary?.provider,
      concrete_provider:
        payload.concrete_provider ?? summary?.concrete_provider,
      model: summary?.model,
      model_version: summary?.model_version,
      mode: summary?.mode,
      lifecycle_status: payload.status ?? node.status,
      qc_disposition: payload.qc_disposition,
      task_id: payload.task_id ?? summary?.task_id,
      provider_error_code: payload.error_code,
      provider_error_message: payload.error_message,
      provider_error_category: payload.provider_error_category,
      provider_error_origin: payload.provider_error_origin,
      provider_error_display_message: payload.provider_error_display_message,
      provider_error_support_codes: payload.provider_error_support_codes,
    });
    if (!attempt) continue;
    const next = {
      ...existing,
      attempts: [...(existing.attempts ?? []), attempt],
    };
    byScene.set(sceneId, next);
    byScene.set(String(sceneId), next);
  }
  return byScene;
}

function preferCandidate(
  next: JourneyAuditCandidate,
  prev: JourneyAuditCandidate,
): boolean {
  const nextAccepted = next.disposition === "accepted";
  const prevAccepted = prev.disposition === "accepted";
  if (nextAccepted !== prevAccepted) return nextAccepted;
  return next.attempt >= prev.attempt;
}

/**
 * Merge the manifest map (full text, authoritative) with the journey map
 * (fallback). Manifest wins per scene; journey fills any scene the manifest is
 * missing.
 */
export function mergeSceneI2V(
  manifest: Map<string | number, SceneI2VAudit>,
  journey: Map<string | number, SceneI2VAudit>,
): Map<string | number, SceneI2VAudit> {
  const merged = new Map<string | number, SceneI2VAudit>(journey);
  for (const [key, value] of manifest) {
    const journeyValue = merged.get(key);
    merged.set(key, {
      ...journeyValue,
      ...value,
      // Older manifests contain the effective prompt but predate attempts[].
      // Keep their durable fields while filling the missing attempt trail from
      // journey events so fallback outcomes and exact models stay visible.
      attempts: value.attempts?.length
        ? value.attempts
        : journeyValue?.attempts,
    });
  }
  return merged;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Resolve, fetch and parse the echo-state manifest for a session. Returns the
 * per-scene I2V map plus a status. Degrades gracefully (empty map, no throw)
 * when no manifest is resolvable or the echo predates the manifest.
 */
export function useEchoStateManifest(
  session: EchoflicksVideoSession | null | undefined,
  media: EchoflicksSessionMedia[],
): EchoStateManifestState {
  const source = resolveManifestSource(session, media);
  const urlState = useMediaUrl(source ?? undefined);
  const hasSource = Boolean(source);
  const manifestUrl = source ? urlState.url : null;

  const [state, setState] = useState<EchoStateManifestState>({
    status: source ? "loading" : "unavailable",
    byScene: new Map(),
    body: null,
  });

  useEffect(() => {
    if (!hasSource) {
      setState({ status: "unavailable", byScene: new Map(), body: null });
      return;
    }
    if (!manifestUrl) {
      setState((prev) =>
        prev.status === "loaded"
          ? prev
          : { status: "loading", byScene: new Map(), body: null },
      );
      return;
    }

    let active = true;
    setState({ status: "loading", byScene: new Map(), body: null });
    fetch(manifestUrl)
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`Manifest request failed with ${response.status}`);
        }
        return response.json();
      })
      .then((manifest) => {
        if (!active) return;
        setState({
          status: "loaded",
          byScene: parseManifestI2V(manifest),
          body: manifest,
        });
      })
      .catch((error) => {
        if (!active) return;
        // The manifest is best-effort (absent on old / manifest-off / failed
        // echoes), so a 403/404 is "unavailable", not a hard error.
        setState({
          status: "unavailable",
          byScene: new Map(),
          body: null,
          message:
            error instanceof Error ? error.message : "Could not load manifest",
        });
      });

    return () => {
      active = false;
    };
  }, [hasSource, source?.storage_bucket, source?.storage_path, manifestUrl]);

  return state;
}
